import React, { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { apiConnectorGet, apiConnectorPost } from "../../services/apiconnector";
import { endpoint } from "../../services/urls";
import toast from "react-hot-toast";

const fmtDate = (str) => {
  if (!str) return "—";
  return new Date(str).toLocaleString("en-US", {
    month: "short", day: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

export default function CustomerCare() {
  const [message, setMessage] = useState("");
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data: tickets = [], isLoading } = useQuery({
    queryKey: ["my_tickets", page],
    queryFn: async () => {
      const res = await apiConnectorPost(endpoint.get_user_messages, {
        page,
        count: 10,
        order: "DESC",
      });
      return res?.data?.result?.data ?? [];
    },
    staleTime: 30 * 1000,
  });

  const { mutate: submitMessage, isPending: submitting } = useMutation({
    mutationFn: () =>
      apiConnectorPost(endpoint.user_message, { message: message.trim() }),
    onSuccess: (res) => {
      if (res?.data?.success || String(res?.data?.success) === "true") {
        toast.success(res?.data?.message || "Message sent!");
        setMessage("");
        queryClient.invalidateQueries({ queryKey: ["my_tickets"] });
      } else {
        toast.error(res?.data?.message || "Failed to send message.");
      }
    },
    onError: () => toast.error("Something went wrong. Please try again."),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Please enter a message.");
      return;
    }
    submitMessage();
  };

  return (
    <div
      className="min-h-screen bg-[#040d12] text-[#00A0FF] px-4 pb-[110px] relative overflow-hidden"
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      {/* Background glows */}
      <div
        className="fixed -top-28 -left-16 w-96 h-96 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(0,229,255,0.07) 0%, transparent 70%)" }}
      />
      <div
        className="fixed -bottom-24 -right-12 w-80 h-80 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(0,180,200,0.05) 0%, transparent 70%)" }}
      />

      {/* Header */}
      <header className="max-w-[680px] mx-auto pt-6 mb-6">
        <p className="text-[10px] tracking-[0.2em] text-[#00A0FF]/60 mb-1.5 font-mono">HELP CENTER</p>
        <h1 className="text-2xl font-bold text-[#00A0FF] tracking-tight font-mono">Customer Support</h1>
        <p className="text-slate-500 text-xs mt-1">
          Submit your query and our support team will reply shortly.
        </p>
      </header>

      {/* Compose form */}
      <div className="max-w-[680px] mx-auto mb-8">
        <div className="rounded-2xl border border-cyan-400/10 bg-[rgba(6,18,26,0.85)] backdrop-blur-md overflow-hidden">
          <div className="h-[3px] w-full" style={{ background: "linear-gradient(90deg,#00e5ff,#0088aa)" }} />
          <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-4">
            <label className="text-[11px] tracking-[0.15em] text-[#00A0FF]/70 font-mono uppercase">
              Your Message
            </label>
            <textarea
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe your issue or question..."
              className="w-full bg-[#040d12] border border-cyan-400/15 rounded-xl px-4 py-3 text-slate-200 text-sm placeholder:text-slate-600 focus:outline-none focus:border-[#00A0FF]/40 resize-none transition-colors"
            />
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] text-slate-600">{message.length} chars</span>
              <button
                type="submit"
                disabled={submitting}
                className="font-mono text-[12px] font-bold px-6 py-2.5 rounded-xl bg-[#00A0FF]/15 border border-[#00A0FF]/35 text-[#00A0FF] hover:bg-[#00A0FF]/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {submitting ? "Sending…" : "Submit Query"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* History */}
      <div className="max-w-[680px] mx-auto">
        <p className="text-[10px] tracking-[0.2em] text-slate-500 font-mono mb-4 uppercase">
          Your Tickets
        </p>

        {isLoading && (
          <div className="flex flex-col gap-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-28 rounded-2xl bg-cyan-400/5 border border-cyan-400/10 animate-pulse" />
            ))}
          </div>
        )}

        {!isLoading && tickets.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-14 h-14 rounded-full bg-cyan-400/5 border border-cyan-400/10 flex items-center justify-center">
              <i className="ri-customer-service-2-line text-2xl text-cyan-400/30" />
            </div>
            <p className="font-mono text-[11px] tracking-[0.15em] text-cyan-400/30 uppercase">
              No tickets yet
            </p>
          </div>
        )}

        {!isLoading && tickets.length > 0 && (
          <div className="flex flex-col gap-4">
            {tickets.map((t) => {
              const replied = Number(t.tr54_status) === 1;
              return (
                <div
                  key={t.tr54_id}
                  className="rounded-2xl border border-cyan-400/10 bg-[rgba(6,18,26,0.85)] backdrop-blur-md overflow-hidden"
                >
                  <div
                    className="h-[3px] w-full"
                    style={{
                      background: replied
                        ? "linear-gradient(90deg,#22c55e,#16a34a)"
                        : "linear-gradient(90deg,#f59e0b,#b45309)",
                    }}
                  />

                  <div className="px-5 py-4 flex flex-col gap-3">
                    {/* Top row */}
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[11px] text-cyan-400/60">
                        #{String(t.tr54_id).padStart(4, "0")}
                      </span>
                      <span
                        className={`font-mono text-[10px] px-2.5 py-0.5 rounded-full border tracking-wider font-bold
                          ${replied
                            ? "bg-emerald-400/10 text-emerald-400 border-emerald-400/25"
                            : "bg-amber-400/10 text-amber-400 border-amber-400/25"}`}
                      >
                        {replied ? "REPLIED" : "PENDING"}
                      </span>
                    </div>

                    {/* User message bubble */}
                    <div>
                      <p className="text-[9px] text-slate-600 tracking-[0.1em] uppercase mb-1.5">Your Message</p>
                      <div className="bg-[#0b1e2b] border border-cyan-400/[0.07] rounded-xl px-4 py-3 text-slate-300 text-sm leading-relaxed">
                        {t.tr54_user_msg}
                      </div>
                      <p className="font-mono text-[9px] text-slate-600 mt-1 text-right">
                        {fmtDate(t.tr54_msg_date)}
                      </p>
                    </div>

                    {/* Admin reply bubble */}
                    {replied && t.tr54_reply && (
                      <div>
                        <p className="text-[9px] text-emerald-400/60 tracking-[0.1em] uppercase mb-1.5">
                          Support Reply
                        </p>
                        <div className="bg-emerald-400/5 border border-emerald-400/15 rounded-xl px-4 py-3 text-emerald-100 text-sm leading-relaxed">
                          {t.tr54_reply}
                        </div>
                        {t.tr54_reply_date && (
                          <p className="font-mono text-[9px] text-emerald-400/40 mt-1">
                            {fmtDate(t.tr54_reply_date)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');
      `}</style>
    </div>
  );
}
